import React, { useState, useMemo, useEffect } from 'react';
import { DataGrid, DataGridProps, GridColDef, GridValidRowModel, GridRenderCellParams } from '@mui/x-data-grid';
import { Box, Card, CardContent, CardActions, Typography, Stack, Divider } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useIsMobile } from '@hooks/useIsMobile';
import IconButton from '@mui/material/IconButton';

export type ResponsiveGridColDef<R extends GridValidRowModel = any> = GridColDef<R> & {
    showInMobile?: boolean;
};

interface ResponsiveDataGridProps<R extends GridValidRowModel> extends Omit<DataGridProps<R>, 'columns'> {
    columns: ResponsiveGridColDef<R>[];
    searchValue?: string; // Prop agregada para manejar la búsqueda de forma unificada
}

export function ResponsiveDataGrid<R extends GridValidRowModel>(props: ResponsiveDataGridProps<R>) {
    const { columns, rows = [], searchValue = '', ...dataGridProps } = props;
    const isMobile = useIsMobile();

    // Ensure rows is always an array
    const safeRows = Array.isArray(rows) ? rows : [];

    // Hooks must always be called before any conditional returns (Rules of Hooks)
    const { getRowId, initialState } = dataGridProps;

    // Extraer configuración de paginación del initialState
    const initialPagination = initialState?.pagination?.paginationModel;
    const [page, setPage] = useState(initialPagination?.page ?? 0);
    const [pageSize] = useState(initialPagination?.pageSize ?? 10);

    // Filtramos solo las columnas que el desarrollador marcó para mostrar en móvil
    const mobileColumns = columns.filter((col) => col.showInMobile);

    // Separamos la columna de acciones (si existe) para ponerla en el footer de la tarjeta
    const actionColumn = mobileColumns.find((col) => col.field === 'actions');
    const dataColumns = mobileColumns.filter((col) => col.field !== 'actions');

    // Lógica de búsqueda manual para la vista de tarjetas móviles
    const filteredMobileRows = useMemo(() => {
        if (!searchValue.trim()) return safeRows;

        const lowerSearch = searchValue.toLowerCase();
        return safeRows.filter((row) => {
            return dataColumns.some((col) => {
                // SOLUCIÓN: Pasar un objeto que simula GridValueGetterParams
                const value = col.valueGetter
                    ? col.valueGetter({
                          row,
                          value: row[col.field],
                          field: col.field,
                          colDef: col
                      } as any)
                    : row[col.field];

                return String(value ?? '')
                    .toLowerCase()
                    .includes(lowerSearch);
            });
        });
    }, [safeRows, searchValue, dataColumns]);

    // Si cambian los filtros de búsqueda, devolvemos al usuario a la primera página de las cards
    useEffect(() => {
        setPage(0);
    }, [searchValue]);

    const totalPages = Math.max(1, Math.ceil(filteredMobileRows.length / pageSize));

    // Calcular las filas paginadas basándose en las filas ya filtradas
    const paginatedRows = useMemo(() => {
        const start = page * pageSize;
        const end = start + pageSize;
        return filteredMobileRows.slice(start, end);
    }, [filteredMobileRows, page, pageSize]);

    // 1. Vista de Escritorio: Renderiza DataGrid estándar
    if (!isMobile) {
        return (
            <DataGrid
                columns={columns as GridColDef<R>[]}
                rows={safeRows}
                {...dataGridProps}
                // Inyectamos el valor de búsqueda al filterModel nativo de DataGrid
                filterModel={{
                    ...dataGridProps.filterModel,
                    items: dataGridProps.filterModel?.items || [],
                    quickFilterValues: searchValue ? [searchValue] : []
                }}
            />
        );
    }

    // 2. Vista Móvil: Renderiza lista de Tarjetas dinámicas con paginación
    return (
        <Box overflow="auto" height="100%">
            {!filteredMobileRows || filteredMobileRows.length === 0 ? (
                <Box p={2} textAlign="center">
                    <Typography color="text.secondary">No se encontraron resultados</Typography>
                </Box>
            ) : (
                <>
                    <Stack spacing={2} pb={2}>
                        {paginatedRows.map((row) => {
                            const rowId = getRowId ? getRowId(row) : (row as any).uid || (row as any).id;

                            return (
                                <Card key={rowId} variant="outlined" sx={{ padding: 0, overflow: 'hidden' }}>
                                    <CardContent sx={{ padding: 0 }}>
                                        <Stack spacing={0}>
                                            {dataColumns.map((col, index) => {
                                                const value = col.valueGetter
                                                    ? col.valueGetter({
                                                          row,
                                                          value: row[col.field],
                                                          field: col.field,
                                                          colDef: col
                                                      } as any)
                                                    : row[col.field];

                                                const fakeCellParams: GridRenderCellParams<any, any, any> = {
                                                    row,
                                                    value,
                                                    field: col.field,
                                                    id: rowId,
                                                    rowNode: {} as any,
                                                    colDef: col as any,
                                                    cellMode: 'view',
                                                    api: {} as any,
                                                    hasFocus: false,
                                                    tabIndex: 0
                                                };

                                                const renderedContent = col.renderCell
                                                    ? col.renderCell(fakeCellParams)
                                                    : value;

                                                const fakeHeaderParams: any = {
                                                    field: col.field,
                                                    colDef: col
                                                };

                                                const renderedHeader = col.renderHeader ? (
                                                    col.renderHeader(fakeHeaderParams)
                                                ) : (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        fontWeight={600}
                                                    >
                                                        {col.headerName || col.field}:
                                                    </Typography>
                                                );

                                                return (
                                                    <React.Fragment key={col.field}>
                                                        <Stack
                                                            direction="row"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            spacing={1}
                                                            sx={{ px: 1.5, py: 1 }}
                                                        >
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                {React.isValidElement(renderedHeader) ? (
                                                                    React.cloneElement(
                                                                        renderedHeader as React.ReactElement,
                                                                        {
                                                                            sx: {
                                                                                ...(renderedHeader as any).props?.sx,
                                                                                fontSize: '0.75rem',
                                                                                lineHeight: 1.2
                                                                            }
                                                                        }
                                                                    )
                                                                ) : (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{ lineHeight: 1.2 }}
                                                                    >
                                                                        {renderedHeader}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                                                                {React.isValidElement(renderedContent) ? (
                                                                    React.cloneElement(
                                                                        renderedContent as React.ReactElement,
                                                                        {
                                                                            sx: {
                                                                                ...(renderedContent as any).props?.sx,
                                                                                fontSize: '0.75rem',
                                                                                wordBreak: 'break-word',
                                                                                textAlign: 'right',
                                                                                lineHeight: 1.2
                                                                            }
                                                                        }
                                                                    )
                                                                ) : (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            wordBreak: 'break-word',
                                                                            display: 'block',
                                                                            textAlign: 'right',
                                                                            lineHeight: 1.2
                                                                        }}
                                                                    >
                                                                        {renderedContent}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Stack>
                                                        {index < dataColumns.length - 1 && (
                                                            <Divider sx={{ mx: -1.5 }} />
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </Stack>
                                    </CardContent>

                                    {actionColumn && (
                                        <Stack>
                                            <Divider />
                                            <CardActions disableSpacing sx={{ justifyContent: 'flex-end', p: 0 }}>
                                                {actionColumn.renderCell &&
                                                    actionColumn.renderCell({
                                                        row,
                                                        value: undefined,
                                                        field: 'actions',
                                                        id: rowId,
                                                        rowNode: {} as any,
                                                        colDef: actionColumn as any,
                                                        cellMode: 'view',
                                                        api: {} as any,
                                                        hasFocus: false,
                                                        tabIndex: 0
                                                    })}
                                            </CardActions>
                                        </Stack>
                                    )}
                                </Card>
                            );
                        })}
                    </Stack>
                    <Box sx={{ px: 2, pb: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                {filteredMobileRows.length} resultado{filteredMobileRows.length !== 1 ? 's' : ''}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <IconButton
                                    disabled={page === 0}
                                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                                <Typography variant="body2" color="text.secondary">
                                    {page + 1} / {totalPages}
                                </Typography>
                                <IconButton
                                    disabled={page >= totalPages - 1}
                                    onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Box>
                </>
            )}
        </Box>
    );
}
